import { z } from 'zod';
import { MANUAL_PAYMENT_METHODS, OUTGOING_PAYMENT_KINDS, OUTGOING_PAYMENT_METHODS, OUTGOING_PAYMENT_STATUSES, } from '../constants/expenses.js';
import { emailSchema, moneyCentsSchema, patchableSchema, uuidSchema } from './common.js';
const isoDate = z.iso.date();
const positiveCents = moneyCentsSchema.refine((n) => n > 0, 'Enter an amount greater than zero');
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
// ------------------------------------------------------------- categories
export const expenseCategorySchema = z.object({
    id: uuidSchema,
    name: z.string(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
});
export const createExpenseCategorySchema = z.object({
    name: z.string().trim().min(1).max(80),
    sortOrder: z.number().int().min(0).max(10_000).default(100),
});
export const updateExpenseCategorySchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(10_000),
})
    .partial();
// ----------------------------------------------------------------- payees
const payeeBaseSchema = z.object({
    name: z.string().trim().min(1).max(120),
    email: emailSchema.nullable().default(null),
    phone: optionalText(40),
    zelleHandle: optionalText(120),
    defaultMethod: z.enum(MANUAL_PAYMENT_METHODS).nullable().default(null),
    notes: optionalText(1000),
    isActive: z.boolean().default(true),
});
export const createPayeeSchema = payeeBaseSchema;
export const updatePayeeSchema = patchableSchema(payeeBaseSchema);
export const payeeSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    zelleHandle: z.string().nullable(),
    defaultMethod: z.enum(OUTGOING_PAYMENT_METHODS).nullable(),
    notes: z.string().nullable(),
    isActive: z.boolean(),
});
// ------------------------------------------------------ outgoing payments
const outgoingPaymentBaseSchema = z.object({
    kind: z.enum(OUTGOING_PAYMENT_KINDS),
    vendorId: uuidSchema.nullable().default(null),
    staffUserId: uuidSchema.nullable().default(null),
    payeeId: uuidSchema.nullable().default(null),
    /** Free-text recipient for an expense with no vendor on file, e.g. "Landlord". */
    payeeLabel: optionalText(120),
    categoryId: uuidSchema.nullable().default(null),
    /** Vendor payments only: the order or the Purchasing invoice this settles. */
    purchaseOrderId: uuidSchema.nullable().default(null),
    invoiceRef: optionalText(120),
    amountCents: positiveCents,
    /** Set (with `paidOn`) to record it as already paid; omit for a bill that is due. */
    method: z.enum(MANUAL_PAYMENT_METHODS).nullable().default(null),
    reference: optionalText(120),
    paidOn: isoDate.nullable().default(null),
    dueOn: isoDate.nullable().default(null),
    note: optionalText(500),
    /** When paid against a purchase order, also mark that order (and its cost lines) paid. */
    markOrderPaid: z.boolean().default(false),
    /**
     * t1223: also settle the unpaid office-supplies bills entered with this order's
     * invoice. The amount covers both parts; the supplies' share is split off.
     */
    payInvoiceExpenses: z.boolean().default(false),
});
/** The shape rules the database CHECKs also enforce, so the user gets a message not a 500. */
function checkPaymentShape(v, ctx) {
    const need = (ok, path, message) => {
        if (!ok)
            ctx.addIssue({ code: 'custom', message, path: [path] });
    };
    if (v.kind === 'vendor')
        need(!!v.vendorId, 'vendorId', 'Choose a vendor');
    if (v.kind === 'staff')
        need(!!v.staffUserId, 'staffUserId', 'Choose a staff member');
    if (v.kind === 'payee')
        need(!!v.payeeId, 'payeeId', 'Choose a freelancer');
    if (v.kind === 'expense') {
        need(!!v.categoryId, 'categoryId', 'Choose a category');
        need(!!(v.vendorId || v.payeeId || v.payeeLabel), 'payeeLabel', 'Say who it is paid to');
    }
    if (v.kind !== 'vendor') {
        need(!v.purchaseOrderId && !v.invoiceRef, 'purchaseOrderId', 'Only vendor payments link to purchases');
    }
    need(!!v.method === !!v.paidOn, 'method', 'A paid entry needs both a method and a date');
    if (v.payInvoiceExpenses) {
        need(v.kind === 'vendor' && !!v.purchaseOrderId && !!v.method, 'payInvoiceExpenses', 'Paying the office supplies needs a paid vendor payment against an order');
    }
}
export const createOutgoingPaymentSchema = outgoingPaymentBaseSchema.superRefine(checkPaymentShape);
export const updateOutgoingPaymentSchema = patchableSchema(outgoingPaymentBaseSchema.omit({ kind: true, markOrderPaid: true, payInvoiceExpenses: true }));
export const payOutgoingPaymentSchema = z.object({
    method: z.enum(MANUAL_PAYMENT_METHODS),
    reference: optionalText(120),
    paidOn: isoDate,
    markOrderPaid: z.boolean().default(false),
});
export const voidOutgoingPaymentSchema = z.object({
    reason: z.string().trim().min(1, 'Say why').max(300),
});
export const outgoingPaymentListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    kind: z.enum(OUTGOING_PAYMENT_KINDS).optional(),
    status: z.enum(OUTGOING_PAYMENT_STATUSES).optional(),
    categoryId: uuidSchema.optional(),
    vendorId: uuidSchema.optional(),
    staffUserId: uuidSchema.optional(),
    payeeId: uuidSchema.optional(),
    purchaseOrderId: uuidSchema.optional(),
    /** t1223: the office-supplies bills entered with this order's invoice. */
    invoicePurchaseOrderId: uuidSchema.optional(),
    /** Inclusive range on the paid date, falling back to the due date for unpaid rows. */
    from: isoDate.optional(),
    to: isoDate.optional(),
});
export const outgoingPaymentDocumentMetaSchema = z.object({
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int(),
    createdAt: z.iso.datetime(),
});
export const outgoingPaymentSchema = z.object({
    id: uuidSchema,
    number: z.string(),
    kind: z.enum(OUTGOING_PAYMENT_KINDS),
    status: z.enum(OUTGOING_PAYMENT_STATUSES),
    /** Display name of whoever or whatever it was for. */
    recipient: z.string(),
    vendorId: uuidSchema.nullable(),
    staffUserId: uuidSchema.nullable(),
    payeeId: uuidSchema.nullable(),
    payeeLabel: z.string().nullable(),
    categoryId: uuidSchema.nullable(),
    categoryName: z.string().nullable(),
    purchaseOrderId: uuidSchema.nullable(),
    purchaseOrderNumber: z.string().nullable(),
    /** t1223: set on the office-supplies part of a split invoice — the order it was entered with. */
    invoicePurchaseOrderId: uuidSchema.nullable(),
    invoicePurchaseOrderNumber: z.string().nullable(),
    /** t1223: the vendor payment that settled this expense along with its order. */
    paidWithPaymentId: uuidSchema.nullable(),
    invoiceRef: z.string().nullable(),
    amountCents: z.number().int(),
    method: z.enum(OUTGOING_PAYMENT_METHODS).nullable(),
    reference: z.string().nullable(),
    paidOn: isoDate.nullable(),
    dueOn: isoDate.nullable(),
    voidReason: z.string().nullable(),
    recurringExpenseId: uuidSchema.nullable(),
    note: z.string().nullable(),
    document: outgoingPaymentDocumentMetaSchema.nullable(),
    createdAt: z.iso.datetime(),
});
export const outgoingPaymentSummaryQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
});
const summaryRowSchema = z.object({
    key: z.string(),
    label: z.string(),
    paidCents: z.number().int(),
    outstandingCents: z.number().int(),
    count: z.number().int(),
});
export const outgoingPaymentSummarySchema = z.object({
    paidCents: z.number().int(),
    outstandingCents: z.number().int(),
    byKind: z.array(summaryRowSchema),
    byCategory: z.array(summaryRowSchema),
    /** Staff, freelancers and vendors, each with their own paid total. */
    byPerson: z.array(summaryRowSchema.extend({ kind: z.enum(OUTGOING_PAYMENT_KINDS), id: uuidSchema })),
});
/** What a vendor order still owes: its line costs + shipping + tax, less paid payments. */
export const purchaseOrderBalanceSchema = z.object({
    purchaseOrderId: uuidSchema,
    poNumber: z.string(),
    totalCents: z.number().int(),
    paidCents: z.number().int(),
    balanceCents: z.number().int(),
    /** t1223: office-supplies bills entered with this order's invoice (not part of the totals above). */
    invoiceExpenses: z.array(z.object({
        id: uuidSchema,
        number: z.string(),
        amountCents: z.number().int(),
        status: z.enum(OUTGOING_PAYMENT_STATUSES),
    })),
});
// ------------------------------------------------------ recurring expenses
const recurringBaseSchema = z.object({
    label: z.string().trim().min(1).max(120),
    categoryId: uuidSchema,
    vendorId: uuidSchema.nullable().default(null),
    payeeId: uuidSchema.nullable().default(null),
    payeeLabel: optionalText(120),
    amountCents: positiveCents,
    defaultMethod: z.enum(MANUAL_PAYMENT_METHODS).nullable().default(null),
    /** 1–28 so every month has the day. */
    dayOfMonth: z.number().int().min(1).max(28),
    startsOn: isoDate,
    isActive: z.boolean().default(true),
    note: optionalText(500),
});
export const createRecurringExpenseSchema = recurringBaseSchema.refine((v) => !!(v.vendorId || v.payeeId || v.payeeLabel), { message: 'Say who it is paid to', path: ['payeeLabel'] });
export const updateRecurringExpenseSchema = patchableSchema(recurringBaseSchema);
export const recurringExpenseSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    categoryId: uuidSchema,
    categoryName: z.string(),
    vendorId: uuidSchema.nullable(),
    payeeId: uuidSchema.nullable(),
    payeeLabel: z.string().nullable(),
    recipient: z.string(),
    amountCents: z.number().int(),
    defaultMethod: z.enum(OUTGOING_PAYMENT_METHODS).nullable(),
    dayOfMonth: z.number().int(),
    startsOn: isoDate,
    isActive: z.boolean(),
    note: z.string().nullable(),
});
//# sourceMappingURL=expenses.js.map